<template>
  <div class="tabs">
    <div class="tabs__slider-wrapper">
      <span ref="leftInd" class="tabs__left-ind"></span>
      <span ref="rightInd" class="tabs__right-ind"></span>
      <div ref="tabsNavSlider" class="tabs__nav-slider">
        <ul ref="tabsHeader" class="tabs__header">
          <li
            class="tabs__tab-item"
            v-for="(tab, index) in tabs"
            :key="tab.title"
            @click="selectTab(index)"
            :class="{ _selected: index == selectedIndex }"
            ref="tabsNavItem"
            role="button"
          >
            <div
              class="tabs__tab-label"
              v-if="tabLabelType"
              v-html="getTabLabel(tab.title)"
            ></div>
            <div class="tabs__tab-label" v-else>
              <span class="tabs__tab-label-text" v-html="tab.title"></span>
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div class="tabs__content">
      <slot />
    </div>
  </div>
</template>

<script>
export default {
  name: "a-tabs",
  props: {
    tabLabelType: String,
  },
  data: () => ({
    selectedIndex: 0,
    tabs: [],
    tabsWidth: 0,
    tabsSliderWidth: 0,
    leftPos: 0,
  }),
  methods: {
    selectTab(i) {
      let that = this;
      this.selectedIndex = i;
      this.tabs.forEach((tab, index) => {
        tab.isActive = index === i;
        tab.selectedIndex = that.selectedIndex;
      });
    },
    getTabLabel(title) {
      return (
        "<" +
        this.tabLabelType +
        " class='tabs__tab-label-text'>" +
        title +
        "</" +
        this.tabLabelType +
        ">"
      );
    },
    mouseScroll() {
      const slider = this.$refs.tabsNavSlider;
      let that = this;
      let isDown = false;
      let startX;
      let scrollLeft;

      slider.addEventListener(
        "mousedown",
        (e) => {
          isDown = true;
          startX = e.pageX - slider.offsetLeft;
          scrollLeft = slider.scrollLeft;
        },
        { passive: true }
      );
      slider.addEventListener(
        "mouseleave",
        () => {
          isDown = false;
        },
        { passive: true }
      );
      slider.addEventListener(
        "mouseup",
        () => {
          isDown = false;
        },
        { passive: true }
      );
      slider.addEventListener("mousemove", (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - slider.offsetLeft;
        const walk = x - startX;
        slider.scrollLeft = scrollLeft - walk;
        that.setInds();
      });
    },
    touchScroll() {
      const slider = this.$refs.tabsNavSlider;
      let that = this;
      let detectScrollEnded = function () {
        let currentScroll = slider.scrollLeft;

        function testIfScrolling() {
          if (slider.scrollLeft !== currentScroll) {
            window.requestAnimationFrame(testIfScrolling);
            that.setInds();
          } else {
            that.setInds();
          }
        }
        window.requestAnimationFrame(testIfScrolling);
      };
      slider.addEventListener("touchend", detectScrollEnded, { passive: true });
      slider.addEventListener(
        "touchmove",
        () => {
          that.setInds();
        },
        { passive: true }
      );
    },
    calculateTabSliderWidth() {
      this.tabsSliderWidth = this.$refs.tabsNavSlider.clientWidth;
    },
    getNavWidth() {
      const item = this.$refs.tabsNavItem;
      let itemsWidthSum = 0;

      for (let i = 0; i < this.tabs.length; i++) {
        itemsWidthSum += parseInt(item[i].clientWidth);
      }
      this.tabsWidth = itemsWidthSum;
      this.setInds();
    },
    setInds() {
      const slider = this.$refs.tabsNavSlider;
      const leftInd = this.$refs.leftInd;
      const rightInd = this.$refs.rightInd;
      const visibleClass = "_visible";
      this.leftPos = slider.scrollLeft;

      if (this.leftPos != undefined) {
        if (
          this.tabsSliderWidth < this.tabsWidth &&
          this.leftPos < this.tabsWidth - this.tabsSliderWidth
        ) {
          rightInd.classList.add(visibleClass);
        } else {
          rightInd.classList.remove(visibleClass);
        }

        if (this.leftPos > 0) {
          leftInd.classList.add(visibleClass);
        } else {
          leftInd.classList.remove(visibleClass);
        }
      }
    },
  },
  created() {
    this.tabs = this.$children;
    window.addEventListener("resize", this.calculateTabSliderWidth, {
      passive: true,
    });
  },
  destroyed() {
    window.removeEventListener("resize", this.calculateTabSliderWidth, {
      passive: true,
    });
  },
  updated() {
    this.getNavWidth();
  },
  mounted() {
    this.selectTab(0);
    this.$nextTick(() => {
      this.calculateTabSliderWidth();
      this.mouseScroll();
      this.touchScroll();
    });
  },
  watch: {
    tabs: function () {
      this.selectTab(0);
    },
    tabsSliderWidth: function () {
      this.setInds();
    },
  },
};
</script>

<style lang="scss" scoped>
.tabs {
  &__slider-wrapper {
    margin-bottom: 24px;
    position: relative;

    &::after {
      background-color: $border-color;
      content: "";
      display: block;
      height: 1px;
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
    }
  }

  &__left-ind,
  &__right-ind {
    background-color: $blue-dark;
    border: 2px solid #fff;
    border-radius: 50%;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    top: 26%;
    height: 22px;
    width: 22px;
    z-index: 2;
    transition: opacity 0.15s ease-in-out;

    &._visible {
      opacity: 1;
    }

    &::after {
      content: "";
      display: block;
      position: absolute;
      top: 6px;
      width: 6px;
      height: 6px;
      border-top: 1px solid #fff;
      border-left: 1px solid #fff;
    }
  }

  &__left-ind {
    left: -10px;

    &::after {
      left: 7px;
      transform: rotate(-45deg);
    }
  }

  &__right-ind {
    right: -10px;

    &::after {
      right: 7px;
      transform: rotate(135deg);
    }
  }

  &__nav-slider {
    overflow-x: auto;
    overflow: -moz-scrollbars-none;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__header {
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;
    position: relative;
  }

  &__tab-item {
    cursor: pointer;
    display: inline-block;
    padding: 12px 14px;
    position: relative;
    white-space: nowrap;
    user-select: none;

    &::before,
    &::after {
      content: "";
      display: block;
      height: 3px;
      position: absolute;
      bottom: 0;
      left: 0px;
      right: 0px;
      z-index: 2;
      transition: background-color ease-in-out 0.1s;
    }

    &:not(&._selected) {
      @media (pointer: fine) {
        &:hover {
          &::before {
            background-color: $gray-20;
          }
        }
      }
    }

    &._selected {
      &::before {
        background-color: $blue-light;
      }
    }
  }
}

::v-deep .tabs {
  &__tab-label-text {
    @include font-m();
    font-size: 18px;
    margin-bottom: 0;
  }
}
</style>
